import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, ErrorState } from '@ui-kit';
import { useHasRole } from '../../_shared/hooks/useCurrentUser';
import { useCodeGroupList } from '../hooks/useCodeGroupList';
import { useCodeItems } from '../hooks/useCodeItems';
import { useCreateCodeGroup } from '../hooks/useCreateCodeGroup';
import { useDeleteCodeGroup } from '../hooks/useDeleteCodeGroup';
import { useUpdateCodeGroup } from '../hooks/useUpdateCodeGroup';
import { useCreateCodeItem } from '../hooks/useCreateCodeItem';
import { useUpdateCodeItem } from '../hooks/useUpdateCodeItem';
import { useDeleteCodeItem } from '../hooks/useDeleteCodeItem';
import { CodeGroupTree } from '../components/CodeGroupTree';
import { CodeItemTable } from '../components/CodeItemTable';
import { CodeGroupCreateDialog } from '../components/CodeGroupCreateDialog';
import { CodeGroupEditDialog } from '../components/CodeGroupEditDialog';
import { CodeGroupDeleteConfirm } from '../components/CodeGroupDeleteConfirm';
import { CodeItemCreateDialog } from '../components/CodeItemCreateDialog';
import { CodeItemDeleteConfirm } from '../components/CodeItemDeleteConfirm';
import type { CommonCodeGroupResponse, CommonCodeValueResponse, ApiError } from '../types/code';
import type { CodeGroupCreateFormValues, CodeGroupUpdateFormValues } from '../schema/codeSchema';
import type { CodeItemCreateFormValues, CodeItemUpdateFormValues } from '../schema/codeSchema';

const DEFAULT_PAGE_SIZE = 50;

export function CommonCodePage() {
  const { t } = useTranslation();

  // Round-EXEC #397: 임원 샌드박스 가드
  const isExec = useHasRole('ROLE_EXEC');
  const isAdmin = useHasRole('ROLE_ADMIN');
  const isExecSandbox = isExec && !isAdmin;

  // 선택된 그룹
  const [selectedGroupCode, setSelectedGroupCode] = useState<string | null>(null);

  // 그룹 목록 쿼리
  const groupListQuery = useCodeGroupList({
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    sort: 'groupCode,asc',
  });

  // 선택된 그룹의 항목 쿼리
  const itemsQuery = useCodeItems(selectedGroupCode);

  // 그룹 Mutation
  const createGroupMutation = useCreateCodeGroup();
  const updateGroupMutation = useUpdateCodeGroup();
  const deleteGroupMutation = useDeleteCodeGroup();

  // 항목 Mutation
  const createItemMutation = useCreateCodeItem();
  const updateItemMutation = useUpdateCodeItem();
  const deleteItemMutation = useDeleteCodeItem();

  // Dialog/Confirm 상태
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [createGroupError, setCreateGroupError] = useState<string | undefined>();
  // #438: groupCode 필드 인라인 에러 (중복 409 등). banner 메시지와 동일 텍스트.
  const [createGroupFieldError, setCreateGroupFieldError] = useState<string | undefined>();

  const [editGroupTarget, setEditGroupTarget] = useState<CommonCodeGroupResponse | null>(null);
  const [editGroupError, setEditGroupError] = useState<string | undefined>();

  const [deleteGroupTarget, setDeleteGroupTarget] = useState<CommonCodeGroupResponse | null>(null);
  const [deleteGroupError, setDeleteGroupError] = useState<string | undefined>();

  const [itemDialogMode, setItemDialogMode] = useState<'create' | 'edit'>('create');
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [itemDialogTarget, setItemDialogTarget] = useState<CommonCodeValueResponse | null>(null);
  const [itemDialogError, setItemDialogError] = useState<string | undefined>();

  const [deleteItemTarget, setDeleteItemTarget] = useState<CommonCodeValueResponse | null>(null);
  const [deleteItemError, setDeleteItemError] = useState<string | undefined>();

  const groups = groupListQuery.data?.content ?? [];

  // 첫 로드 시 첫 그룹 자동 선택
  const handleGroupSelect = useCallback((groupCode: string) => {
    setSelectedGroupCode(groupCode);
  }, []);

  // 그룹 생성
  const handleCreateGroupSubmit = useCallback(
    (values: CodeGroupCreateFormValues) => {
      setCreateGroupError(undefined);
      setCreateGroupFieldError(undefined);
      createGroupMutation.mutate(
        {
          groupCode: values.groupCode,
          groupName: values.groupName,
          description: values.description,
          systemManaged: values.systemManaged,
        },
        {
          onSuccess: (data) => {
            setCreateGroupOpen(false);
            setSelectedGroupCode(data.groupCode);
          },
          onError: (err: unknown) => {
            const apiErr = err as ApiError;
            const message = apiErr.message ?? t('commonCode.groupCreateError.default');
            setCreateGroupError(message);
            // #438: groupCode 관련 4xx 에러는 같은 메시지를 인라인 필드 에러로도 노출.
            // CODE-409-001 그룹 코드 중복, CODE-400-001 유효성, CODE-423-001 EXEC sandbox.
            if (apiErr.code === 'CODE-409-001' || apiErr.code === 'CODE-400-001' || apiErr.code === 'CODE-423-001') {
              setCreateGroupFieldError(message);
            }
          },
        }
      );
    },
    [createGroupMutation, t]
  );

  // 그룹 수정 (#379)
  const handleEditGroupSubmit = useCallback(
    (values: CodeGroupUpdateFormValues) => {
      if (!editGroupTarget) return;
      setEditGroupError(undefined);
      updateGroupMutation.mutate(
        { groupCode: editGroupTarget.groupCode, body: values },
        {
          onSuccess: () => {
            setEditGroupTarget(null);
          },
          onError: (err: unknown) => {
            const apiErr = err as ApiError;
            setEditGroupError(apiErr.message ?? t('commonCode.groupUpdateError.default'));
          },
        }
      );
    },
    [editGroupTarget, updateGroupMutation, t]
  );

  // 그룹 삭제
  const handleDeleteGroupConfirm = useCallback(() => {
    if (!deleteGroupTarget) return;
    setDeleteGroupError(undefined);
    deleteGroupMutation.mutate(deleteGroupTarget.groupCode, {
      onSuccess: () => {
        setDeleteGroupTarget(null);
        // 삭제된 그룹이 선택된 경우 선택 해제
        if (selectedGroupCode === deleteGroupTarget.groupCode) {
          setSelectedGroupCode(null);
        }
      },
      onError: (err: unknown) => {
        const apiErr = err as ApiError;
        setDeleteGroupError(apiErr.message ?? t('commonCode.groupDeleteError.default'));
      },
    });
  }, [deleteGroupMutation, deleteGroupTarget, selectedGroupCode]);

  // 항목 생성/수정 제출
  const handleItemDialogSubmit = useCallback(
    (values: CodeItemCreateFormValues | CodeItemUpdateFormValues) => {
      if (!selectedGroupCode) return;
      setItemDialogError(undefined);

      if (itemDialogMode === 'create') {
        const createValues = values as CodeItemCreateFormValues;
        createItemMutation.mutate(
          {
            groupCode: selectedGroupCode,
            body: {
              codeValue: createValues.codeValue,
              defaultLabel: createValues.defaultLabel,
              labelKey: createValues.labelKey,
              sortOrder: createValues.sortOrder,
              useYn: createValues.useYn,
              extraAttrs: createValues.extraAttrs,
            },
          },
          {
            onSuccess: () => {
              setItemDialogOpen(false);
              setItemDialogTarget(null);
            },
            onError: (err: unknown) => {
              const apiErr = err as ApiError;
              setItemDialogError(apiErr.message ?? t('commonCode.itemCreateError.default'));
            },
          }
        );
      } else {
        if (!itemDialogTarget) return;
        const updateValues = values as CodeItemUpdateFormValues;
        updateItemMutation.mutate(
          {
            groupCode: selectedGroupCode,
            itemCode: itemDialogTarget.codeValue,
            body: {
              defaultLabel: updateValues.defaultLabel,
              labelKey: updateValues.labelKey,
              sortOrder: updateValues.sortOrder,
              useYn: updateValues.useYn,
              extraAttrs: updateValues.extraAttrs,
            },
          },
          {
            onSuccess: () => {
              setItemDialogOpen(false);
              setItemDialogTarget(null);
            },
            onError: (err: unknown) => {
              const apiErr = err as ApiError;
              setItemDialogError(apiErr.message ?? t('commonCode.itemUpdateError.default'));
            },
          }
        );
      }
    },
    [selectedGroupCode, itemDialogMode, itemDialogTarget, createItemMutation, updateItemMutation]
  );

  // 항목 삭제
  const handleDeleteItemConfirm = useCallback(() => {
    if (!deleteItemTarget || !selectedGroupCode) return;
    setDeleteItemError(undefined);
    deleteItemMutation.mutate(
      { groupCode: selectedGroupCode, itemCode: deleteItemTarget.codeValue },
      {
        onSuccess: () => {
          setDeleteItemTarget(null);
        },
        onError: (err: unknown) => {
          const apiErr = err as ApiError;
          setDeleteItemError(apiErr.message ?? t('commonCode.itemDeleteError.default'));
        },
      }
    );
  }, [deleteItemMutation, deleteItemTarget, selectedGroupCode]);

  const selectedGroup = groups.find((g) => g.groupCode === selectedGroupCode);

  // 권한 없음 처리 (최상위 에러)
  const isForbidden =
    groupListQuery.isError &&
    (groupListQuery.error as unknown as ApiError)?.code === 'AUTH-403-001';

  if (isForbidden) {
    return (
      <div
        style={{
          padding: 'var(--spacing-6)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
        }}
      >
        <ErrorState
          title={t('commonCode.error.forbidden')}
          message={t('commonCode.error.forbiddenMessage')}
          onRetry={() => void groupListQuery.refetch()}
        />
      </div>
    );
  }

  return (
    <div
      style={{ padding: 'var(--spacing-6)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}
    >
      {/* 페이지 헤더 — R7-071: subtitle 추가 */}
      <PageHeader
        title={t('commonCode.page.title')}
        subtitle={t('commonCode.pageHeader.subtitle')}
        breadcrumb={<span>{t('commonCode.page.breadcrumb')}</span>}
      />

      {/* Round-EXEC #397: 임원 샌드박스 안내 배너 */}
      {isExecSandbox && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--spacing-3)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            background: 'color-mix(in srgb, var(--color-warning) 10%, var(--color-surface-background))',
            border: '1px solid color-mix(in srgb, var(--color-warning) 40%, transparent)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          <span aria-hidden="true" style={{ fontSize: '1.1em', flexShrink: 0 }}>&#128274;</span>
          <div>
            <strong style={{ display: 'block', marginBottom: 'var(--spacing-1)' }}>
              {t('commonCode.exec.banner.title')}
            </strong>
            <span style={{ color: 'var(--color-text-secondary)' }}>
              {t('commonCode.exec.banner.description')}
            </span>
          </div>
        </div>
      )}

      {/* Split Layout — 좌: 그룹 트리, 우: 항목 테이블 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(220px, 280px) 1fr',
          gap: 'var(--spacing-4)',
          minHeight: '60vh',
          // 반응형: 768px 미만에서 단일 컬럼
        }}
        className="common-code-split"
      >
        {/* 좌측 — 그룹 트리 */}
        <div
          style={{
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface-background)',
            overflow: 'hidden',
          }}
        >
          <CodeGroupTree
            groups={groups}
            selectedGroupCode={selectedGroupCode}
            isLoading={groupListQuery.isLoading}
            isError={groupListQuery.isError}
            error={groupListQuery.error}
            isExecSandbox={isExecSandbox}
            onSelect={(code) => {
              handleGroupSelect(code);
            }}
            onAddGroup={() => {
              setCreateGroupError(undefined);
              setCreateGroupOpen(true);
            }}
            onEditGroup={(group) => {
              setEditGroupError(undefined);
              setEditGroupTarget(group);
            }}
            onDeleteGroup={(group) => {
              setDeleteGroupError(undefined);
              setDeleteGroupTarget(group);
            }}
            onRetry={() => void groupListQuery.refetch()}
          />
        </div>

        {/* 우측 — 항목 테이블 */}
        <div
          style={{
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface-background)',
            overflow: 'hidden',
          }}
        >
          <CodeItemTable
            groupCode={selectedGroupCode}
            groupName={selectedGroup?.groupName}
            systemManaged={selectedGroup?.systemManaged}
            items={itemsQuery.data ?? []}
            isLoading={itemsQuery.isLoading}
            isError={itemsQuery.isError}
            error={itemsQuery.error}
            isExecSandbox={isExecSandbox}
            onAddItem={() => {
              setItemDialogMode('create');
              setItemDialogTarget(null);
              setItemDialogError(undefined);
              setItemDialogOpen(true);
            }}
            onEditItem={(item) => {
              setItemDialogMode('edit');
              setItemDialogTarget(item);
              setItemDialogError(undefined);
              setItemDialogOpen(true);
            }}
            onDeleteItem={(item) => {
              setDeleteItemError(undefined);
              setDeleteItemTarget(item);
            }}
            onRetry={() => void itemsQuery.refetch()}
          />
        </div>
      </div>

      {/* 그룹 생성 다이얼로그 */}
      <CodeGroupCreateDialog
        open={createGroupOpen}
        onClose={() => {
          setCreateGroupOpen(false);
          setCreateGroupError(undefined);
          setCreateGroupFieldError(undefined);
        }}
        onSubmit={handleCreateGroupSubmit}
        isLoading={createGroupMutation.isPending}
        errorMessage={createGroupError}
        groupCodeFieldError={createGroupFieldError}
        isExecSandbox={isExecSandbox}
      />

      {/* 그룹 수정 다이얼로그 (#379) */}
      <CodeGroupEditDialog
        open={!!editGroupTarget}
        group={editGroupTarget}
        onClose={() => setEditGroupTarget(null)}
        onSubmit={handleEditGroupSubmit}
        isLoading={updateGroupMutation.isPending}
        errorMessage={editGroupError}
      />

      {/* 그룹 삭제 확인 */}
      <CodeGroupDeleteConfirm
        open={!!deleteGroupTarget}
        group={deleteGroupTarget}
        onClose={() => setDeleteGroupTarget(null)}
        onConfirm={handleDeleteGroupConfirm}
        isLoading={deleteGroupMutation.isPending}
        errorMessage={deleteGroupError}
      />

      {/* 항목 생성/수정 다이얼로그 */}
      {selectedGroupCode && (
        <CodeItemCreateDialog
          open={itemDialogOpen}
          mode={itemDialogMode}
          groupCode={selectedGroupCode}
          initialData={itemDialogTarget}
          onClose={() => {
            setItemDialogOpen(false);
            setItemDialogTarget(null);
          }}
          onSubmit={handleItemDialogSubmit}
          isLoading={createItemMutation.isPending || updateItemMutation.isPending}
          errorMessage={itemDialogError}
        />
      )}

      {/* 항목 삭제 확인 */}
      <CodeItemDeleteConfirm
        open={!!deleteItemTarget}
        item={deleteItemTarget}
        groupCode={selectedGroupCode ?? ''}
        onClose={() => setDeleteItemTarget(null)}
        onConfirm={handleDeleteItemConfirm}
        isLoading={deleteItemMutation.isPending}
        errorMessage={deleteItemError}
      />

      {/* 반응형 CSS */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 767px) {
          .common-code-split {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
